import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileSearch, Download, RefreshCw, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { FinanceWorkspaceShell } from './FinanceWorkspaceShell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

type BankStatement = {
  id: string;
  bank_account_id: string;
  file_name: string;
  line_count: number;
  status: string;
  currency: string;
};

type BankReconPack = {
  statement: {
    id: string;
    fileName: string;
    status: string;
    statementDate: string | null;
    periodFrom: string | null;
    periodTo: string | null;
    openingBalance: number | null;
    closingBalance: number | null;
    currency: string;
    lineCount: number;
    createdAt: string | null;
    account: {
      id: string;
      accountName: string;
      accountNumber: string;
      bankName: string;
      glAccountId: string | null;
    };
  };
  summary: {
    totalLines: number;
    matchedLines: number;
    partialLines: number;
    unmatchedLines: number;
    manualLines: number;
    autoLines: number;
    grossStatementAmount: number;
    totalMatchedAmount: number;
    totalUnmatchedAmount: number;
    statementNetMovement: number;
    ledgerNetMovement: number;
    movementDifference: number;
    matchRate: number;
    readyForClose: boolean;
  };
  sessions: Array<{
    id: string;
    status: string;
    periodFrom: string;
    periodTo: string;
    bankBalance: number;
    bookBalance: number | null;
    difference: number | null;
    totalLines: number;
    matchedLines: number;
    unmatchedLines: number;
    completedAt: string | null;
    completedBy: string | null;
    notes: string | null;
    createdAt: string | null;
  }>;
  latestApprovedSession: string | null;
  latestCompletedSession: string | null;
  matchedLines: ReconLine[];
  partialLines: ReconLine[];
  unmatchedLines: ReconLine[];
  exportedAt: string;
};

type ReconLine = {
  id: string;
  lineNumber: number;
  txnDate: string;
  valueDate: string | null;
  description: string;
  reference: string | null;
  debit: number;
  credit: number;
  balance: number | null;
  amount: number;
  absoluteAmount: number;
  matchStatus: string;
  sourceStatus: string;
  matchConfidence: number | null;
  matchNotes: string | null;
  matchedAmount: number;
  unmatchedAmount: number;
  matchedEntries: Array<{
    ledgerEntryId: string;
    entryNumber: string;
    entryType: string;
    entryDate: string | null;
    narration: string | null;
    matchedAmount: number;
    matchMethod: string;
    matchConfidence: number | null;
    notes: string | null;
  }>;
};

function fmtNumber(value: number | null | undefined): string {
  const num = Number(value ?? 0);
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
}

function buildCsv(pack: BankReconPack): string {
  const rows: string[][] = [
    ['section', 'statement', pack.statement.fileName],
    ['bank_account', pack.statement.account.accountName, pack.statement.account.bankName],
    ['match_rate', `${pack.summary.matchRate}%`, ''],
    ['total_lines', String(pack.summary.totalLines), ''],
    ['matched_lines', String(pack.summary.matchedLines), ''],
    ['partial_lines', String(pack.summary.partialLines), ''],
    ['unmatched_lines', String(pack.summary.unmatchedLines), ''],
    ['statement_net_movement', String(pack.summary.statementNetMovement), ''],
    ['ledger_net_movement', String(pack.summary.ledgerNetMovement), ''],
    ['movement_difference', String(pack.summary.movementDifference), ''],
    [],
    ['status', 'line_number', 'txn_date', 'description', 'reference', 'amount', 'matched_amount', 'unmatched_amount', 'matched_entries'],
  ];

  const allLines = [...pack.unmatchedLines, ...pack.partialLines, ...pack.matchedLines];
  for (const line of allLines) {
    rows.push([
      line.matchStatus,
      String(line.lineNumber),
      line.txnDate,
      line.description,
      line.reference ?? '',
      String(line.amount),
      String(line.matchedAmount),
      String(line.unmatchedAmount),
      line.matchedEntries.map((entry) => `${entry.entryNumber}:${entry.matchedAmount}`).join(' | '),
    ]);
  }

  return rows
    .map((row) => row.map((cell) => `"${String(cell ?? '').replaceAll('"', '""')}"`).join(','))
    .join('\n');
}

function downloadCsv(fileName: string, content: string) {
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function MatchStatusBadge({ status }: { status: string }) {
  if (status === 'matched') {
    return <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Matched</Badge>;
  }
  if (status === 'partial') {
    return <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Partial</Badge>;
  }
  return <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">Unmatched</Badge>;
}

export default function BankReconPackPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statements, setStatements] = useState<BankStatement[]>([]);
  const [selectedStatementId, setSelectedStatementId] = useState(searchParams.get('statementId') ?? '');
  const [pack, setPack] = useState<BankReconPack | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatements = useCallback(async () => {
    const rows = await api.get<BankStatement[]>('/banking/statements');
    const eligible = (rows ?? []).filter((row) => ['parsed', 'reconciling', 'reconciled'].includes(row.status));
    setStatements(eligible);
    return eligible;
  }, []);

  const fetchPack = useCallback(async (statementId: string) => {
    if (!statementId) {
      setPack(null);
      return;
    }
    const res = await api.get<BankReconPack>(`/banking/reconciliation-pack?statementId=${encodeURIComponent(statementId)}`);
    setPack(res ?? null);
  }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const eligible = await fetchStatements();
      const preferredCandidate = selectedStatementId || eligible[0]?.id || '';
      const preferred = eligible.some((statement) => statement.id === preferredCandidate)
        ? preferredCandidate
        : (eligible[0]?.id || '');
      if (preferred) {
        setSelectedStatementId(preferred);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('statementId', preferred);
          return next;
        }, { replace: true });
        await fetchPack(preferred);
      } else {
        setPack(null);
      }
    } catch (error) {
      console.error('Bank recon pack bootstrap failed', error);
      setPack(null);
    } finally {
      setLoading(false);
    }
  }, [fetchPack, fetchStatements, selectedStatementId, setSearchParams]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const handleSelectStatement = useCallback(async (value: string) => {
    setSelectedStatementId(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('statementId', value);
      return next;
    }, { replace: true });
    setRefreshing(true);
    try {
      await fetchPack(value);
    } finally {
      setRefreshing(false);
    }
  }, [fetchPack, setSearchParams]);

  const handleRefresh = useCallback(async () => {
    if (!selectedStatementId) return;
    setRefreshing(true);
    try {
      await fetchStatements();
      await fetchPack(selectedStatementId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to refresh reconciliation pack');
    } finally {
      setRefreshing(false);
    }
  }, [fetchPack, fetchStatements, selectedStatementId]);

  const handleExport = useCallback(() => {
    if (!pack) return;
    downloadCsv(
      `bank-recon-pack-${pack.statement.fileName.replace(/[^a-zA-Z0-9_-]+/g, '-').toLowerCase()}.csv`,
      buildCsv(pack),
    );
    toast.success('Bank recon pack exported');
  }, [pack]);

  const checklistItems = useMemo(() => {
    if (!pack) return ['Select a parsed or reconciled statement to build the close evidence pack.'];
    const items: string[] = [];
    if (pack.summary.unmatchedLines > 0) items.push(`${pack.summary.unmatchedLines} statement line(s) still unmatched and must return to reconciliation.`);
    if (pack.summary.partialLines > 0) items.push(`${pack.summary.partialLines} line(s) are only partially matched and need allocation review.`);
    if (Math.abs(pack.summary.movementDifference) > 0.01) items.push(`Statement-vs-ledger net movement difference is ${fmtNumber(pack.summary.movementDifference)}.`);
    if (pack.summary.unmatchedLines === 0 && pack.summary.partialLines === 0) items.push('All statement lines are cleared or fully matched for this statement.');
    if (pack.latestApprovedSession) items.push('At least one approved reconciliation session exists for this statement.');
    return items;
  }, [pack]);

  return (
    <FinanceWorkspaceShell
      title="Bank Recon Pack"
      subtitle="Statement proof · close evidence · exception drill-down"
      Icon={FileSearch}
      iconClassName="text-violet-600"
      moduleBadge="AC operating layer"
      domainBadge="Bank close evidence"
      agentTitle="Turn reconciliation into close evidence"
      agentSummary="This pack separates operational matching from close evidence. Use it to prove which bank lines cleared, which remain open, and whether statement movement still differs from ledger movement for the selected bank statement."
      checklistTitle="Bank Recon Evidence Checklist"
      checklistStatus={pack?.summary.readyForClose ? 'Ready for close review' : 'Exceptions still open'}
      checklistNote="The pack is statement-scoped. It is designed for month-end review, audit support, and controller sign-off rather than daily matching."
      checklistItems={checklistItems}
      quickActions={[
        { label: 'Reconciliation', to: '/finance/reconciliation' },
        { label: 'Bank Statements', to: '/finance/bank-statements' },
        { label: 'Close Cockpit', to: '/finance/close-cockpit' },
        { label: 'Audit Report', to: '/finance/reports/audit-report', variant: 'secondary' },
      ]}
      headerActions={(
        <>
          <Select value={selectedStatementId} onValueChange={(value) => void handleSelectStatement(value)}>
            <SelectTrigger className="w-[280px]">
              <SelectValue placeholder="Select statement" />
            </SelectTrigger>
            <SelectContent>
              {statements.map((statement) => (
                <SelectItem key={statement.id} value={statement.id}>
                  {statement.file_name} · {statement.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => void handleRefresh()} disabled={loading || refreshing || !selectedStatementId}>
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={handleExport} disabled={!pack}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          {selectedStatementId ? (
            <Button asChild size="sm">
              <Link to="/finance/reconciliation">
                Open Reconciliation
              </Link>
            </Button>
          ) : null}
        </>
      )}
    >
      {loading ? (
        <Card><CardContent className="p-8 text-sm text-muted-foreground">Loading bank recon pack…</CardContent></Card>
      ) : !pack ? (
        <Card><CardContent className="p-8 text-sm text-muted-foreground">No eligible bank statement found. Upload and parse a statement first.</CardContent></Card>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Statement</div><div className="mt-1 text-base font-semibold">{pack.statement.fileName}</div><div className="mt-1 text-xs text-muted-foreground">{pack.statement.account.accountName}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Match Rate</div><div className={`mt-1 text-2xl font-semibold ${pack.summary.readyForClose ? 'text-emerald-700' : 'text-amber-700'}`}>{pack.summary.matchRate}%</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Matched</div><div className="mt-1 text-2xl font-semibold text-emerald-700">{pack.summary.matchedLines}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Partial</div><div className="mt-1 text-2xl font-semibold text-amber-700">{pack.summary.partialLines}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Unmatched</div><div className="mt-1 text-2xl font-semibold text-rose-700">{pack.summary.unmatchedLines}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Movement Diff</div><div className={`mt-1 text-2xl font-semibold ${Math.abs(pack.summary.movementDifference) > 0.01 ? 'text-rose-700' : 'text-emerald-700'}`}>{fmtNumber(pack.summary.movementDifference)}</div></CardContent></Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
            <Card>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">Statement Proof</div>
                    <div className="text-xs text-muted-foreground">Header evidence and movement comparison used during month-end close.</div>
                  </div>
                  {pack.summary.readyForClose ? (
                    <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                      <CheckCircle2 className="mr-1 h-3 w-3" />
                      Ready
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                      <AlertTriangle className="mr-1 h-3 w-3" />
                      Open exceptions
                    </Badge>
                  )}
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl border bg-white px-3 py-3">
                    <div className="text-xs text-muted-foreground">Statement period</div>
                    <div className="mt-1 text-sm font-semibold">{fmtDate(pack.statement.periodFrom)} to {fmtDate(pack.statement.periodTo)}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Statement date {fmtDate(pack.statement.statementDate)}</div>
                  </div>
                  <div className="rounded-xl border bg-white px-3 py-3">
                    <div className="text-xs text-muted-foreground">Balances</div>
                    <div className="mt-1 text-sm font-semibold">Opening {fmtNumber(pack.statement.openingBalance)}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Closing {fmtNumber(pack.statement.closingBalance)} {pack.statement.currency}</div>
                  </div>
                  <div className="rounded-xl border bg-white px-3 py-3">
                    <div className="text-xs text-muted-foreground">Statement movement</div>
                    <div className="mt-1 text-sm font-semibold">{fmtNumber(pack.summary.statementNetMovement)}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Gross activity {fmtNumber(pack.summary.grossStatementAmount)}</div>
                  </div>
                  <div className="rounded-xl border bg-white px-3 py-3">
                    <div className="text-xs text-muted-foreground">Ledger movement</div>
                    <div className="mt-1 text-sm font-semibold">{fmtNumber(pack.summary.ledgerNetMovement)}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Difference {fmtNumber(pack.summary.movementDifference)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-4">
                <div>
                  <div className="text-sm font-semibold">Session Trail</div>
                  <div className="text-xs text-muted-foreground">Completed and approved reconciliation sessions attached to this statement.</div>
                </div>
                {pack.sessions.length === 0 ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-muted-foreground">
                    No reconciliation session has been recorded for this statement yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {pack.sessions.slice(0, 4).map((session) => (
                      <div key={session.id} className="rounded-xl border bg-white px-3 py-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-medium">{session.status.toUpperCase()}</div>
                          {pack.latestApprovedSession === session.id ? (
                            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                              <ShieldCheck className="mr-1 h-3 w-3" />
                              Approved
                            </Badge>
                          ) : null}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {fmtDate(session.periodFrom)} to {fmtDate(session.periodTo)}
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">
                          {session.matchedLines} matched · {session.unmatchedLines} unmatched · diff {fmtNumber(session.difference)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Unmatched & Partial Exceptions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[...pack.unmatchedLines, ...pack.partialLines].length === 0 ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
                    No unmatched or partial lines remain for this statement.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">Open</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[...pack.unmatchedLines, ...pack.partialLines].map((line) => (
                          <TableRow key={line.id}>
                            <TableCell className="text-xs text-muted-foreground">{line.lineNumber}</TableCell>
                            <TableCell className="whitespace-nowrap text-sm">{fmtDate(line.txnDate)}</TableCell>
                            <TableCell className="max-w-[280px] text-sm">
                              <div className="truncate">{line.description}</div>
                              <div className="text-xs text-muted-foreground">{line.reference ?? 'No reference'}</div>
                            </TableCell>
                            <TableCell><MatchStatusBadge status={line.matchStatus} /></TableCell>
                            <TableCell className="text-right">{fmtNumber(line.amount)}</TableCell>
                            <TableCell className="text-right font-medium text-rose-700">{fmtNumber(line.unmatchedAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Matched Evidence Lines</CardTitle>
              </CardHeader>
              <CardContent>
                {pack.matchedLines.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No matched lines yet for this statement.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Ledger proof</TableHead>
                          <TableHead className="text-right">Matched</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pack.matchedLines.slice(0, 20).map((line) => (
                          <TableRow key={line.id}>
                            <TableCell className="text-xs text-muted-foreground">{line.lineNumber}</TableCell>
                            <TableCell className="max-w-[240px] text-sm">
                              <div className="truncate">{line.description}</div>
                              <div className="text-xs text-muted-foreground">{fmtDate(line.txnDate)}</div>
                            </TableCell>
                            <TableCell className="text-xs">
                              <div className="space-y-1">
                                {line.matchedEntries.map((entry) => (
                                  <div key={`${line.id}-${entry.ledgerEntryId}`} className="rounded-lg border bg-muted/20 px-2 py-1">
                                    <div className="font-medium">{entry.entryNumber} · {entry.entryType}</div>
                                    <div className="text-muted-foreground">{entry.narration ?? 'No narration'} · {entry.matchMethod}</div>
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-medium text-emerald-700">{fmtNumber(line.matchedAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </FinanceWorkspaceShell>
  );
}
